import { useState } from "react";
import { Share2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact, in-page social share row for any specific URL+text (a short, an
 * episode, a collection — anything). Different from `AboutShareBlock` which
 * always shares the homepage and requires an explicit personal note.
 *
 * Props:
 *   url: full canonical URL of the page being shared
 *   text: pre-filled message (will be appended with the URL on Whatsapp/Telegram)
 *   testid: prefix for data-testids
 */
export default function ShareButtons({ url, text, testid = "share" }) {
  const [copied, setCopied] = useState(false);
  const safeText = (text || "").trim();
  const fullText = safeText ? `${safeText}\n\n${url}` : url;
  const encodedText = encodeURIComponent(fullText);
  const encodedUrl = encodeURIComponent(url);
  const encodedMsg = encodeURIComponent(safeText || url);

  const SHARES = [
    { id: "whatsapp", label: "WhatsApp", brand: "bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] border-[#25D366]/40", href: `https://wa.me/?text=${encodedText}` },
    { id: "linkedin", label: "LinkedIn", brand: "bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 text-[#4c9beb] border-[#0A66C2]/40", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
    { id: "x", label: "X", brand: "bg-slate-600/20 hover:bg-slate-500/30 text-slate-100 border-slate-500/40", href: `https://twitter.com/intent/tweet?text=${encodedMsg}&url=${encodedUrl}` },
    { id: "facebook", label: "Facebook", brand: "bg-[#1877F2]/20 hover:bg-[#1877F2]/30 text-[#3b82f6] border-[#1877F2]/40", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedMsg}` },
    { id: "telegram", label: "Telegram", brand: "bg-[#229ED9]/20 hover:bg-[#229ED9]/30 text-[#60b8e4] border-[#229ED9]/40", href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedMsg}` },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar — copie manualmente.");
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testid}-buttons`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
        <Share2 className="w-3 h-3" /> Compartilhar
      </div>
      <div className="flex flex-wrap gap-2">
        {SHARES.map((s) => (
          <a
            key={s.id}
            href={s.href}
            target="_blank"
            rel="noreferrer noopener"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition ${s.brand}`}
            data-testid={`${testid}-${s.id}`}
          >
            {s.label}
          </a>
        ))}
        <button
          type="button"
          onClick={copyLink}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-900/60 text-slate-200 hover:border-white hover:bg-slate-800 text-xs font-medium transition"
          data-testid={`${testid}-copy`}
        >
          {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copiado" : "Copiar link"}
        </button>
      </div>
    </div>
  );
}
